import React from 'react';
import {
  Info
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * AIOutputNotice - EU AI Act Aligned Transparency Component
 * 
 * Per-Industry AI Disclaimers - Clean, regulator-aligned, production-ready.
 * 
 * Industry Types:
 * - finance: Finance & Accounting (Invoice, Expense, Xero, Payment)
 * - legal: Legal & Compliance (Contract, Risk, Policy)
 * - health: Health, Safety & People (HR, Safety, QA, Screening)
 * - strategy: Strategy & Operations (Planning, Analysis, Advisory)
 * - generic: Default fallback
 * 
 * EU AI Act Compliance:
 * - Minimal/Limited Risk: Decision-support only
 * - Human Oversight: Required and explicit
 * - No Automated Decisions: Never positioned as autonomous
 * - Transparency: AI use disclosed at point of output
 * - Accountability: Responsibility remains with user
 * 
 * UI Rules:
 * - Small text, neutral icon
 * - Not dismissible
 * - Must not block actions
 * - Partners cannot override or remove disclaimers
 */

// Per-Industry AI Disclaimer Configuration
// Designed to be EU AI Act–aware, GDPR-friendly, non-alarmist
const INDUSTRY_DISCLAIMER_CONFIG = {
  // 💰 Finance & Accounting
  finance: {
    inline: "This output supports financial workflows and analysis. It does not constitute accounting, tax, or financial advice. Review by a qualified professional is recommended before submission or decision-making.",
    tooltip: "AI assists with financial processes. Final decisions require professional review.",
    icon: "💰",
    appliesTo: ["Invoice Processing", "Expense Agents", "Payment Follow-up", "Financial Summaries", "Xero-connected agents"]
  },
  
  // ⚖️ Legal & Compliance
  legal: {
    inline: "This output is provided for informational and drafting support only. It does not constitute legal advice and should not be relied upon as such.",
    tooltip: "AI-assisted content for legal workflows. Professional legal review required.",
    icon: "⚖️",
    appliesTo: ["Contract Review", "Contract Summaries", "Risk Assessment", "Policy & Compliance agents"]
  },
  
  // 🏥 Health, Safety & People (HR, Safety, QA)
  health: {
    inline: "This output supports analysis and documentation. It is not a medical, clinical, or safety determination and should not replace professional judgment.",
    tooltip: "AI supports operational review. Human oversight required for decisions affecting people.",
    icon: "🏥",
    appliesTo: ["Candidate Screening", "Safety Incident Drafting", "Call Quality Analysis", "People & HR agents"]
  },
  
  // 🧠 Strategy & Operations
  strategy: {
    inline: "This output supports strategic thinking and planning. It does not replace professional advice or independent judgment.",
    tooltip: "AI supports decision-making. Final responsibility remains with the user.",
    icon: "🧠",
    appliesTo: ["Strategic Planning", "Business Analysis", "Advisory", "Operations"]
  },
  
  // 🧩 Generic (Default Fallback)
  generic: {
    inline: "This output was generated by AI and should be reviewed by a human before use. It may be incomplete or inaccurate.",
    tooltip: "Man and Machine AI supports decision-making. Final responsibility remains with the user.",
    icon: "🤖",
    appliesTo: ["General AI Agents"]
  },

  // Legacy mappings for backward compatibility
  call_qa: {
    inline: "This analysis was generated by AI based on the uploaded recording. Scores and recommendations reflect industry best-practice patterns and should be reviewed by a human before use.",
    tooltip: "AI analysis supports quality assessment. Final evaluation should be made by qualified personnel.",
    icon: "🎧",
    appliesTo: ["Call Quality Analysis", "Voice Analysis"]
  },
  live_voice: {
    inline: "This agent uses AI to support live conversations. Outcomes may vary and should be monitored by a human, especially for customer-facing interactions.",
    tooltip: "Live AI agents assist with conversations. Human oversight is recommended.",
    icon: "🎙️",
    appliesTo: ["Live Voice Agents", "Booking", "Receptionist", "Scheduler"]
  },
  content: {
    inline: "This content was generated by AI and may require review, editing, or validation before use or publication.",
    tooltip: "AI-generated content should be reviewed and edited before distribution.",
    icon: "📄",
    appliesTo: ["Document & Content Agents", "Blog", "Website Copy", "Proposals"]
  },
  hr: {
    inline: "This output is assistive only and should not be used as the sole basis for employment or compliance decisions.",
    tooltip: "AI supports HR processes. Human judgment is required for employment decisions.",
    icon: "👥",
    appliesTo: ["Candidate Screening", "HR Summaries", "Performance Reviews"]
  },
  sales: {
    inline: "This output supports sales operations and data entry. Accuracy depends on the quality of source data provided.",
    tooltip: "AI assists with sales workflows. Verify data accuracy before actioning.",
    icon: "🔍",
    appliesTo: ["Lead Scoring", "CRM Updates", "Sales Operations"]
  }
};

const AIOutputNotice = ({ 
  variant = 'inline', // 'inline', 'compact', 'footer'
  noticeType = 'generic', // finance, legal, health, strategy, generic (+ legacy types)
  className = '' 
}) => {
  // Get notice config, fallback to generic if type not found
  const config = INDUSTRY_DISCLAIMER_CONFIG[noticeType] || INDUSTRY_DISCLAIMER_CONFIG.generic;
  const noticeText = config.inline;
  const tooltipText = config.tooltip;

  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`inline-flex items-center gap-1.5 text-xs text-[#6B7280] cursor-help ${className}`}>
              <Info size={12} className="text-[#6B7280]" />
              <span>About this AI output</span>
            </div>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="max-w-sm bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-white p-3"
          >
            <p className="text-xs leading-relaxed mb-2">{noticeText}</p>
            <p className="text-xs text-[#6B7280] italic">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={`flex items-start gap-2 py-2 px-3 bg-[rgba(255,255,255,0.02)] border-t border-[rgba(255,255,255,0.05)] ${className}`}>
        <Info size={14} className="text-[#6B7280] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-[#6B7280] leading-relaxed">
            {noticeText}
          </p>
        </div>
      </div>
    );
  }

  // Default: inline variant
  return (
    <TooltipProvider>
      <div className={`flex items-start gap-2 p-3 bg-[rgba(99,102,241,0.05)] border border-[rgba(99,102,241,0.1)] rounded-lg ${className}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 cursor-help flex-shrink-0">
              <Info size={14} className="text-[#6366F1]" />
              <span className="text-xs font-medium text-[#6366F1]">About this AI output</span>
            </div>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="max-w-xs bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-white p-3"
          >
            <p className="text-xs text-[#6B7280] italic">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
        <p className="text-xs text-[#A0AEC0] leading-relaxed flex-1">
          {noticeText}
        </p>
      </div>
    </TooltipProvider>
  );
};

// Export for use in other components
export const AI_INDUSTRY_TYPES = ['finance', 'legal', 'health', 'strategy', 'generic'];
export const AI_NOTICE_TYPES = Object.keys(INDUSTRY_DISCLAIMER_CONFIG);
export const getDisclaimerConfig = (type) => INDUSTRY_DISCLAIMER_CONFIG[type] || INDUSTRY_DISCLAIMER_CONFIG.generic;

export default AIOutputNotice;
